import { DefaultValue } from "../ConstructionAmountCalculator";
import { ConstructionMaterialCalculateInput, FromUnitInput, InputField, ToUnitInput } from "../types";
import UnitId from "./UnitId";

// An insulation EPD declared per 1 m² is declared at R=1, so the thickness that scales its quantity
// correctly is the λ-value, not the layer's real thickness. Shared by the two configs where the
// material is declared per m² and a thickness is asked for, so the two can't drift apart (RTLCA-5121).
// Kept short on purpose: it renders as a footnote in a fixed-height column in the Adjust dimensions
// modal, and a longer version clipped at the bottom edge.
// The label now says "Thickness (m) / λ-value (W/mK)", so this explains why rather than repeating it —
// the same pairing also holds m²-declared materials that are not insulation (RTLCA-5852).
const M2_THICKNESS_DESCRIPTION =
  "Declared per 1 m² at R=1, so the λ-value scales it, not the layer thickness. If it isn't insulation, use its actual thickness.";

const CalculatorConfigs: Array<InputField> = [
  // From: M, M2, M3
  // KG - kg/m^2
  // Row 6
  {
    fromUnitIds: [UnitId.M, UnitId.M2, UnitId.M3, UnitId.KG, UnitId.PIECE],
    toMassUnit: {
      numeratorUnitId: UnitId.KG,
      denominatorUnitId: UnitId.M2,
    },
    toUnitId: UnitId.KG,
    // Declared in kg, so the real thickness is what's wanted here. Spelled out because the old
    // "use thickness from epd" left it open to the R=1 reading that m²-declared EPDs need (RTLCA-5121).
    fields: [
      { label: "length" },
      { label: "thickness", description: "Enter the actual thickness from the EPD." },
      { label: "quantity" },
    ],
    calculate: {
      onLoad: function (_, __, constructionMaterial) {
        const amount = constructionMaterial.amount;
        const length = constructionMaterial.length;
        const thickness = constructionMaterial.thickness || 0;
        const weight = constructionMaterial.amount;

        return {
          amount,
          dimensions: { length, thickness },
          weight,
        };
      },
      getFromCubicMeters: (_, toUnit, constructionMaterial: ConstructionMaterialCalculateInput, amount: number) => {
        const thickness = constructionMaterial.thickness || 0;
        // A material with no thickness yet occupies no volume. Dividing by it returned Infinity, and
        // scaleInhomogeneousLayer multiplies this into every dimension of every material in the
        // layer, so one blank thickness took the whole layer with it.
        if (!thickness) return 0;

        return (amount * toUnit.massValue) / thickness;
      },
      getToCubicMeters: function (_, toUnit, constructionMaterial) {
        const amount = constructionMaterial.amount || 0;
        const massValue = toUnit.massValue;
        const thickness = constructionMaterial.thickness || 0;
        const weight = amount;

        return (thickness / massValue) * weight;
      },
      byInhomogeneousLayerThickness: function (fromUnit, toUnit, constructionMaterial) {
        const thickness = constructionMaterial.thickness || 0;
        const length = constructionMaterial.length || 0;

        const amount = (fromUnit.amount / thickness) * length * toUnit.massValue;
        const weight = amount;

        return {
          amount,
          dimensions: { length, thickness },
          weight,
        };
      },
      byDimensions: function (fromUnit: FromUnitInput, toUnit: ToUnitInput, dimensions: ConstructionMaterialCalculateInput) {
        const length = dimensions.length || 0;
        const thickness = dimensions.thickness || 0;
        const amount = (fromUnit.amount / thickness) * length * toUnit.massValue;
        const weight = amount;

        return {
          amount,
          dimensions: { length, thickness },
          weight,
        };
      },
      byQuantity: function (_, __, constructionMaterial) {
        const amount = constructionMaterial.amount;
        const length = constructionMaterial.length || 0;
        const thickness = constructionMaterial.thickness || 0;
        const weight = constructionMaterial.amount;

        return {
          amount,
          dimensions: { length, thickness },
          weight,
        };
      },
    },
  },
  // From: M, M2, M3
  // KG - kg/m3
  // Row 7
  {
    fromUnitIds: [UnitId.M, UnitId.M2, UnitId.M3, UnitId.KG, UnitId.PIECE],
    toMassUnit: {
      numeratorUnitId: UnitId.KG,
      denominatorUnitId: UnitId.M3,
    },
    toUnitId: UnitId.KG,
    fields: [{ label: "length" }, { label: "quantity" }],
    calculate: {
      onLoad: function (_, __, constructionMaterial) {
        const amount = constructionMaterial.amount;
        const length = constructionMaterial.length || 0;
        const weight = constructionMaterial.amount;

        return {
          amount,
          dimensions: { length },
          weight,
        };
      },
      byInhomogeneousLayerThickness: function (fromUnit, __, constructionMaterial) {
        const inhomogeneousLayerThickness = fromUnit.amount || 0;
        const amount = constructionMaterial.amount * inhomogeneousLayerThickness;
        const length = constructionMaterial.length || 0;
        const weight = constructionMaterial.weight || 0;

        return {
          amount,
          dimensions: { length },
          weight,
        };
      },
      getFromCubicMeters: (_, toUnit, __, amount) => {
        return amount * toUnit.massValue;
      },
      getToCubicMeters: function (_, toUnit, constructionMaterial) {
        return constructionMaterial.amount / toUnit.massValue;
      },
      byDimensions: function (fromUnit, toUnit, dimensions) {
        const length = dimensions.length || 0;
        const inhomogeneousLayerThickness = fromUnit.amount || 0;
        const amount = inhomogeneousLayerThickness * length * toUnit.massValue;
        const weight = amount;

        return {
          amount,
          dimensions: { length },
          weight,
        };
      },
      byQuantity: function (fromUnit, toUnit, constructionMaterial) {
        const amount = constructionMaterial.amount;
        const length = amount / toUnit.massValue / fromUnit.amount;
        const weight = amount;

        return {
          amount,
          dimensions: { length },
          weight,
        };
      },
    },
  },
  // From: M, M2, M3
  // KG - kg/m
  // Row 8
  {
    fromUnitIds: [UnitId.M, UnitId.M2, UnitId.KG, UnitId.PIECE],
    toMassUnit: {
      numeratorUnitId: UnitId.KG,
      denominatorUnitId: UnitId.M,
    },
    toUnitId: UnitId.KG,
    fields: [{ label: "length" }, { label: "quantity" }],
    calculate: {
      onLoad: function (_, __, constructionMaterial) {
        const amount = constructionMaterial.amount;
        const length = constructionMaterial.length || 0;
        const thickness = constructionMaterial.thickness || 0;
        const weight = constructionMaterial.amount;

        return {
          amount,
          dimensions: { length, thickness },
          weight,
        };
      },
      byInhomogeneousLayerThickness: function (fromUnit, toUnit, constructionMaterial) {
        const inhomogeneousLayerThickness = fromUnit.amount || 0;
        const amount = constructionMaterial.amount * inhomogeneousLayerThickness;
        const length = amount / toUnit.massValue / inhomogeneousLayerThickness;
        const weight = constructionMaterial.weight || 0;

        return {
          amount,
          dimensions: { length },
          weight,
        };
      },
      byDimensions: function (fromUnit: FromUnitInput, toUnit: ToUnitInput, dimensions: ConstructionMaterialCalculateInput) {
        const length = dimensions.length || 0;
        const weight = length * toUnit.massValue * fromUnit.amount;
        const amount = weight;

        return {
          amount,
          dimensions: { length },
          weight,
        };
      },
      byQuantity: function (fromUnit, toUnit, constructionMaterial) {
        const amount = constructionMaterial.amount;
        const length = amount / toUnit.massValue / fromUnit.amount;

        const weight = constructionMaterial.amount;

        return {
          amount,
          dimensions: { length },
          weight,
        };
      },
    },
  },
  // From: M, M2, M3
  // KG - kg , kg/psc
  // Row 9
  {
    fromUnitIds: [UnitId.M, UnitId.M2, UnitId.KG, UnitId.PIECE],
    toMassUnit: {
      numeratorUnitId: UnitId.KG,
      denominatorUnitId: UnitId.PIECE,
    },
    toUnitId: UnitId.KG,
    fields: [{ label: "quantity" }],
    calculate: {
      onLoad: function (_, __, constructionMaterial) {
        const amount = constructionMaterial.amount;
        const weight = constructionMaterial.amount;

        return {
          amount,
          dimensions: {},
          weight,
        };
      },
      byQuantity: function (_, __, constructionMaterial) {
        const amount = constructionMaterial.amount;
        const weight = constructionMaterial.amount;

        return {
          amount,
          dimensions: {},
          weight,
        };
      },
    },
  },
  // Row 10
  // From: M, M2, M3
  // PSC - kg/psc
  {
    fromUnitIds: [UnitId.M, UnitId.M2, UnitId.KG, UnitId.PIECE],
    toUnitId: UnitId.PIECE,
    toMassUnit: {
      numeratorUnitId: UnitId.KG,
      denominatorUnitId: UnitId.PIECE,
    },
    fields: [{ label: "quantity" }, { label: "weight" }],
    calculate: {
      onLoad: function (_, toUnit, constructionMaterial) {
        const amount = constructionMaterial.amount;
        const weight = amount * toUnit.massValue;

        return {
          amount,
          dimensions: {},
          weight,
        };
      },
      byInhomogeneousLayerThickness: function (_, __, constructionMaterial) {
        const amount = constructionMaterial.amount;
        const weight = constructionMaterial.weight || 0;

        return {
          amount,
          dimensions: {},
          weight,
        };
      },
      byQuantity: function (_, toUnit, constructionMaterial) {
        const amount = constructionMaterial.amount;
        const weight = toUnit.massValue * amount;

        return {
          amount,
          dimensions: {},
          weight,
        };
      },
      byWeight: function (_: FromUnitInput, toUnit: ToUnitInput, input: ConstructionMaterialCalculateInput) {
        const weight = input.weight || 0;
        const amount = weight / toUnit.massValue;

        return {
          amount,
          dimensions: {},
          weight,
        };
      },
    },
  },
  // From: M, M2, M3
  // m - kg/m
  // Row 11
  {
    fromUnitIds: [UnitId.M, UnitId.M2, UnitId.KG, UnitId.PIECE],
    toMassUnit: {
      numeratorUnitId: UnitId.KG,
      denominatorUnitId: UnitId.M,
    },
    toUnitId: UnitId.M,
    fields: [{ label: "quantity" }],
    calculate: {
      onLoad: function (_, __, constructionMaterial) {
        const amount = constructionMaterial.amount;
        const weight = constructionMaterial.amount;

        return {
          amount,
          dimensions: {},
          weight,
        };
      },
      byInhomogeneousLayerThickness: function (fromUnit, _, constructionMaterial) {
        const inhomogeneousLayerThickness = fromUnit.amount || 0;
        const amount = constructionMaterial.amount * inhomogeneousLayerThickness;
        const weight = constructionMaterial.weight || 0;

        return {
          amount,
          dimensions: {},
          weight,
        };
      },
      byQuantity: function (_, __, constructionMaterial) {
        const amount = constructionMaterial.amount;
        const weight = constructionMaterial.amount;

        return {
          amount,
          dimensions: {},
          weight,
        };
      },
    },
  },
  // From: M, M2, M3
  // M, M2 - kg/m2
  // Row 12
  {
    fromUnitIds: [UnitId.M, UnitId.M2, UnitId.M3, UnitId.KG, UnitId.PIECE],
    toMassUnit: {
      numeratorUnitId: UnitId.KG,
      denominatorUnitId: UnitId.M2,
    },
    toUnitId: UnitId.M2,
    fields: [
      { label: "length" },
      { label: "thickness", description: M2_THICKNESS_DESCRIPTION, meansLambda: true },
      { label: "weight" },
    ],
    calculate: {
      onLoad: function (_, toUnit, constructionMaterial) {
        const amount = constructionMaterial.length || 0;
        const length = amount;
        const thickness = constructionMaterial.thickness || 0;
        const weight = (constructionMaterial.length || 0) * toUnit.massValue;

        return {
          amount,
          dimensions: { length, thickness },
          weight,
        };
      },
      getFromCubicMeters: (_, __, constructionMaterial: ConstructionMaterialCalculateInput, amount: number) => {
        const thickness = constructionMaterial.thickness || 0;
        // See row 6: without this, a blank thickness returns Infinity and scaleInhomogeneousLayer
        // spreads it across the layer.
        if (!thickness) return 0;

        return amount / thickness;
      },
      getToCubicMeters: function (fromUnit, _, constructionMaterial) {
        const length = constructionMaterial.length || 0;
        // const thickness = constructionMaterial.thickness || 0;
        // const amount = (length * fromUnit.amount) / (fromUnit.isInhomogeneousLayer ? thickness : 1);
        const amount = length * fromUnit.amount;

        return amount;
      },
      byDimensions: function (fromUnit: FromUnitInput, toUnit: ToUnitInput, dimensions: ConstructionMaterialCalculateInput) {
        const length = dimensions.length || 0;
        const thickness = dimensions.thickness || 0;
        const amount = (length * fromUnit.amount) / (fromUnit.isInhomogeneousLayer ? thickness : 1);
        const weight = length * toUnit.massValue;

        return {
          amount,
          dimensions: { length, thickness },
          weight,
        };
      },
      byInhomogeneousLayerThickness: function (fromUnit, toUnit, constructionMaterial) {
        if (typeof this.byDimensions === "function") {
          return this.byDimensions(fromUnit, toUnit, constructionMaterial);
        }

        return DefaultValue;
      },
      byWeight: function (fromUnit: FromUnitInput, toUnit: ToUnitInput, input: ConstructionMaterialCalculateInput) {
        const weight = input.weight || 0;
        const amount = weight / toUnit.massValue;
        const length = weight / toUnit.massValue;
        // This inverts byDimensions, which only divides by the thickness for an inhomogeneous layer.
        // For a construction it does not, so there is nothing to invert, and deriving a thickness here
        // silently replaced whatever the user had typed with the construction's declared amount —
        // typing a weight turned a λ-value of 0.037 into 1. Row 14 keeps the entered value the same way.
        const thickness = fromUnit.isInhomogeneousLayer ? (length * fromUnit.amount) / amount : input.thickness || 0;

        return {
          amount,
          dimensions: { length, thickness },
          weight,
        };
      },
    },
  },
  // From: M, M2, M3
  // M3 - kg/m3
  // Row 13
  {
    fromUnitIds: [UnitId.M, UnitId.M2, UnitId.M3, UnitId.KG, UnitId.PIECE],
    toUnitId: UnitId.M3,
    toMassUnit: {
      numeratorUnitId: UnitId.KG,
      denominatorUnitId: UnitId.M3,
    },
    fields: [{ label: "length" }, { label: "weight" }],
    calculate: {
      onLoad: function (_, toUnit, constructionMaterial) {
        const amount = constructionMaterial.amount;
        const length = constructionMaterial.length || 0;
        const weight = amount * toUnit.massValue;

        return {
          amount,
          dimensions: { length },
          weight,
        };
      },
      byDimensions: function (fromUnit: FromUnitInput, toUnit: ToUnitInput, dimensions: ConstructionMaterialCalculateInput) {
        const length = dimensions.length || 0;
        const amount = length * fromUnit.amount;
        const weight = (dimensions.length || 0) * toUnit.massValue * fromUnit.amount;

        return {
          amount,
          dimensions: { length },
          weight,
        };
      },
      byInhomogeneousLayerThickness: function (fromUnit, toUnit, constructionMaterial) {
        const length = constructionMaterial.length || 1;
        const weight = fromUnit.amount * toUnit.massValue * length;
        const amount = length * fromUnit.amount;

        return {
          amount,
          dimensions: { length },
          weight,
        };
      },
      byQuantity: function (_, toUnit, constructionMaterial) {
        const amount = constructionMaterial.amount;
        const weight = amount * toUnit.massValue;

        return {
          amount,
          dimensions: {},
          weight,
        };
      },
      byWeight: function (fromUnit: FromUnitInput, toUnit: ToUnitInput, input: ConstructionMaterialCalculateInput) {
        const weight = input.weight || 0;
        const length = weight / (toUnit.massValue * fromUnit.amount);
        const amount = length * fromUnit.amount;

        return {
          amount,
          dimensions: { length },
          weight,
        };
      },
      getFromCubicMeters: (_, __, ___: ConstructionMaterialCalculateInput, amount: number) => {
        return amount;
      },
      getToCubicMeters: function (_, __, constructionMaterial) {
        return constructionMaterial.amount;
      },
    },
  },
  // From: M, M2, M3
  // M2 - kg/m3
  // Row 14
  {
    fromUnitIds: [UnitId.M, UnitId.M2, UnitId.M3, UnitId.KG, UnitId.PIECE],
    toUnitId: UnitId.M2,
    toMassUnit: {
      numeratorUnitId: UnitId.KG,
      denominatorUnitId: UnitId.M3,
    },
    fields: [
      { label: "quantity" },
      { label: "thickness", hideForHomogeneousLayer: true, description: M2_THICKNESS_DESCRIPTION, meansLambda: true },
      { label: "weight" },
    ],
    calculate: {
      onLoad: function (_, toUnit, constructionMaterial) {
        const amount = constructionMaterial.amount;
        const weight = amount * toUnit.massValue;

        return {
          amount,
          dimensions: {},
          weight,
        };
      },
      byInhomogeneousLayerThickness: function (_, __, constructionMaterial) {
        const amount = constructionMaterial.amount;
        const weight = constructionMaterial.weight || 0;

        return {
          amount,
          dimensions: {},
          weight,
        };
      },
      byDimensions: function (_: FromUnitInput, __: ToUnitInput, constructionMaterial: ConstructionMaterialCalculateInput) {
        const amount = constructionMaterial.amount;
        const thickness = constructionMaterial.thickness || 0;
        const weight = constructionMaterial.weight || 0;

        return {
          amount,
          dimensions: { thickness },
          weight,
        };
      },
      getFromCubicMeters: function (_, __, constructionMaterial, amount) {
        const thickness = constructionMaterial.thickness || 0;
        // See row 6: without this, a blank thickness returns Infinity and scaleInhomogeneousLayer
        // spreads it across the layer.
        if (!thickness) return 0;

        return amount / thickness;
      },
      getToCubicMeters: function (_, __, constructionMaterial) {
        const thickness = constructionMaterial.thickness || 0;
        const amount = constructionMaterial.amount;

        return amount * thickness;
      },
      byQuantity: function (_, toUnit, constructionMaterial) {
        const amount = constructionMaterial.amount;
        const thickness = constructionMaterial.thickness || 0;
        const weight = toUnit.massValue * amount;

        return {
          amount,
          dimensions: { thickness },
          weight,
        };
      },
      byWeight: function (_: FromUnitInput, toUnit: ToUnitInput, input: ConstructionMaterialCalculateInput) {
        const weight = input.weight || 0;
        const amount = weight / toUnit.massValue;
        const thickness = input.thickness || 0;

        return {
          amount,
          dimensions: { thickness },
          weight,
        };
      },
    },
  },
];

export default CalculatorConfigs;
